import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postData, ImageFileUpload } from "../../../utility/Utility";
import CustomLoader from "../../../Components/CustomLoader/CustomLoader";

export default function AddSubcategory() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    image: null,
    category: "",
    status: "active"
  });

  // State for categories from API
  const [categories, setCategories] = useState([]);
  const [imagePreview, setImagePreview] = useState("");

  // Check if we're in edit mode via URL params or navigation state
  useEffect(() => {
    const subCategoryData = location.state?.subCategoryData;

    if (id || subCategoryData) {
      setIsEditMode(true);

      if (subCategoryData) {
        // Set form data from navigation state
        setFormData({
          title: subCategoryData.title || "",
          description: subCategoryData.description || "",
          image: null,
          category: subCategoryData.category?._id || subCategoryData.category || "",
          status: subCategoryData.status || "active"
        });

        // Set image preview if image exists
        if (subCategoryData.image) {
          setImagePreview(subCategoryData.image);
        }
      } else if (id) {
        // Fetch data from API if only ID is provided
        fetchSubcategoryDetails(id);
      }
    }
  }, [id, location.state]);

  // Fetch categories from API
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await postData("api/categories/list", {
        page: 1,
        limit: 10000,
      });

      if (response?.status) {
        setCategories(response?.data || []);
      } else {
        throw new Error(response?.data?.message || "Failed to fetch categories");
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error(
        error.response?.data?.message || error.message || "Error loading categories"
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch subcategory details for edit (when only ID is provided)
  const fetchSubcategoryDetails = async (subcategoryId) => {
    try {
      setLoading(true);
      const response = await postData("api/subcategory/details", {
        subcategoryId: subcategoryId
      });

      if (response?.status) {
        const subcategory = response.data;
        setFormData({
          title: subcategory.title || "",
          description: subcategory.description || "",
          image: null,
          category: subcategory.category?._id || subcategory.category || "",
          status: subcategory.status || "active"
        });

        // Set image preview if image exists
        if (subcategory.image) {
          setImagePreview(subcategory.image);
        }
      } else {
        throw new Error(response?.message || "Failed to fetch subcategory details");
      }
    } catch (error) {
      console.error("Error fetching subcategory details:", error);
      toast.error(
        error.response?.data?.message || error.message || "Error loading subcategory details"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "image" && files && files[0]) {
      const file = files[0];
      setFormData((prev) => ({
        ...prev,
        image: file
      }));

      // Image preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Upload image to API
  const uploadImage = async () => {
    if (!formData.image) return null;

    const formDataObj = new FormData();
    formDataObj.append("file", formData.image);

    try {
      const response = await ImageFileUpload("/api/admin/upload?is_upload=s3", formDataObj);

      if (response?.success) {
        return response.url || response.imageUrl || response.filePath;
      } else {
        toast.error(response?.message || "Image upload failed");
        return null;
      }
    } catch (err) {
      console.error("Image upload error:", err);
      toast.error("Network error during image upload");
      return null;
    }
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validation
      if (!formData.category) {
        throw new Error("Please select a category");
      }

      if (!formData.title.trim()) {
        throw new Error("Please enter a subcategory title");
      }

      // Upload image if selected
      let uploadedImagePath = "";
      if (formData.image) {
        uploadedImagePath = await uploadImage();
        if (!uploadedImagePath) {
          throw new Error("Image upload failed");
        }
      }

      // Prepare payload according to API structure
      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        category: formData.category,
        status: formData.status
      };

      // Add image to payload
      if (uploadedImagePath) {
        payload.image = uploadedImagePath;
      } else if (isEditMode && imagePreview && !formData.image) {
        // Keep existing image if no new image was uploaded in edit mode
        payload.image = imagePreview;
      }

      let response;
      let endpoint;

      if (isEditMode) {
        // Update subcategory - use ID from params or from navigation state
        const subcategoryId = id || location.state?.subCategoryData?._id;
        if (!subcategoryId) {
          throw new Error("Subcategory ID is required for update");
        }

        payload.subcategoryId = subcategoryId;
        endpoint = "/api/subcategory/update";
      } else {
        // Create subcategory
        endpoint = "/api/subcategory/create";
      }

      response = await postData(endpoint, payload);

      if (response?.status) {
        toast.success(`Subcategory ${isEditMode ? 'updated' : 'added'} successfully!`);
        navigate(-1);
      } else {
        throw new Error(response?.message || `Error ${isEditMode ? 'updating' : 'adding'} subcategory`);
      }
    } catch (err) {
      console.error(`Error ${isEditMode ? 'updating' : 'adding'} subcategory:`, err);
      toast.error(err.message || `Failed to ${isEditMode ? 'update' : 'add'} subcategory`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="content-page">
      <CustomLoader loading={loading} />
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      <div className="content">
        <div className="container-fluid">
          {/* Header */}
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <h4 className="fs-18 fw-semibold m-0">
              {isEditMode ? "Edit Subcategory" : "Add Subcategory"}
            </h4>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate(-1)}
              disabled={loading}
            >
              Back
            </button>
          </div>

          {/* Form Card */}
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">Subcategory Details</h5>
                </div>

                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    {/* Category Dropdown */}
                    <div className="row mb-3">
                      <div className="col-md-3">
                        <label htmlFor="category" className="form-label fw-medium">
                          Category*
                        </label>
                      </div>
                      <div className="col-md-6">
                        <select
                          id="category"
                          name="category"
                          className="form-select"
                          value={formData.category}
                          onChange={handleInputChange}
                          required
                          disabled={loading || categories.length === 0}
                        >
                          <option value="">Select a category</option>
                          {categories.map((category) => (
                            <option key={category._id} value={category._id}>
                              {category.title}
                            </option>
                          ))}
                        </select>
                        {loading && categories.length === 0 && (
                          <div className="form-text">Loading categories...</div>
                        )}
                        {!loading && categories.length === 0 && (
                          <div className="form-text text-danger">
                            No categories available. Please add categories first.
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Subcategory Title */}
                    <div className="row mb-3">
                      <div className="col-md-3">
                        <label htmlFor="title" className="form-label fw-medium">
                          Subcategory Title*
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="text"
                          id="title"
                          name="title"
                          className="form-control"
                          placeholder="Enter subcategory title"
                          value={formData.title}
                          onChange={handleInputChange}
                          required
                          minLength={3}
                          maxLength={100}
                          disabled={loading}
                        />
                      </div>
                    </div>

                    {/* Description */}
                    <div className="row mb-3">
                      <div className="col-md-3">
                        <label htmlFor="description" className="form-label fw-medium">
                          Description
                        </label>
                      </div>
                      <div className="col-md-6">
                        <textarea
                          id="description"
                          name="description"
                          className="form-control"
                          placeholder="Enter subcategory description"
                          value={formData.description}
                          onChange={handleInputChange}
                          rows={4}
                          disabled={loading}
                        />
                      </div>
                    </div>

                    {/* Image Upload */}
                    <div className="row mb-3">
                      <div className="col-md-3">
                        <label htmlFor="image" className="form-label fw-medium">
                          Subcategory Image
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="file"
                          id="image"
                          name="image"
                          className="form-control"
                          onChange={handleInputChange}
                          accept="image/*"
                          disabled={loading}
                        />
                        <div className="form-text">
                          Recommended size: 800x600px, Max size: 2MB
                        </div>
                        {imagePreview && (
                          <div className="mt-2">
                            <img
                              src={imagePreview}
                              alt="Subcategory Preview"
                              className="img-thumbnail"
                              style={{ maxWidth: "200px", maxHeight: "200px" }}
                            />
                            {isEditMode && !formData.image && (
                              <div className="form-text mt-1">Existing image</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Status */}
                    <div className="row mb-3">
                      <div className="col-md-3">
                        <label className="form-label fw-medium">Status</label>
                      </div>
                      <div className="col-md-6">
                        <select
                          name="status"
                          className="form-select"
                          value={formData.status}
                          onChange={handleInputChange}
                          disabled={loading}
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="row mt-4">
                      <div className="col-md-9 offset-md-3">
                        <button
                          type="submit"
                          className="btn btn-primary px-4"
                          disabled={loading || categories.length === 0}
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2"></span>
                              {isEditMode ? "Updating..." : "Adding..."}
                            </>
                          ) : (
                            isEditMode ? "Update Subcategory" : "Add Subcategory"
                          )}
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-secondary ms-2"
                          onClick={() => navigate(-1)}
                          disabled={loading}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}